import React from 'react';
import styled from 'styled-components';
import { ArrowForwardSharp } from '@material-ui/icons';
import CheckIcon from '@material-ui/icons/Check';
import { Button } from '@material-ui/core';

import BackButton from '../BackButton';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const Header = styled.header`
  display: flex;
  justify-content: stretch;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid #dddddd;
  padding-bottom: 10px;
  margin-bottom: -10px;
`;

Header.Left = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
`;

Header.Center = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
`;

Header.Right = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: flex-end;
`;

Header.TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

Header.BackButton = styled(BackButton)``;

Header.Title = styled.h1`
  font-family: Poppins;
  font-weight: 400;
  font-style: Regular;
  font-size: 24px;
  margin-bottom: 5px;
  color: #212121;
  max-width: 270px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
`;

Header.Title.Step = styled.span`
  font-weight: 600;
  font-family: inherit;
`;

Header.Description = styled.p`
  font-family: Poppins;
  font-weight: 400;
  font-style: Regular;
  font-size: 15px;
  margin-bottom: 0;
  color: #9b9b9b;
  max-width: 270px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
`;

const NextIcon = styled(ArrowForwardSharp)`
  font-size: 20px !important;
`;

Header.NextButton = styled(Button).attrs({
  size: 'large',
  endIcon: <NextIcon />,
})`
  align-self: start;

  && {
    padding-left: 20px;
    padding-right: 20px;
    font-family: Poppins;
    font-weight: 400;
    font-style: Regular;
    font-size: 16px;
    line-height: 1.2;
    color: #004bc4;
    border: 1px solid #004bc4;
    border-radius: 13px;
    background-color: #fff;
    transition: all 0.3s ease-in;

    &:hover {
      background-color: ${({ isDisabledVisual }) =>
        isDisabledVisual ? '#fff' : '#007ade'};
      color: ${({ isDisabledVisual }) =>
        isDisabledVisual ? '#004bc4' : '#fff'};
    }

    &:disabled {
      pointer-events: all;
      cursor: not-allowed;
      color: #004bc4;
      border-color: #004bc4;
    }

    ${({ isDisabledVisual }) =>
      isDisabledVisual &&
      `
      cursor: not-allowed;
      pointer-events: all;
    `}

    // Override global default styles which set Poppins as font-family for everything (which is pretty bad!)
    > .MuiButton-label {
      font-family: inherit;
    }
  }
`;

Header.EditButton = styled.span`
  color: #004bc4;
  font-family: Poppins;
  font-weight: 400;
  font-style: Regular;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0%;
  text-decoration: underline;
  text-decoration-style: solid;
  text-decoration-offset: 0%;
  text-decoration-thickness: 0%;
  text-decoration-skip-ink: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    text-decoration: none;
  }
`;

export const Body = styled.div`
  flex-grow: 1;
  padding: ${({ withoutVerticalPadding }) =>
      withoutVerticalPadding ? 0 : '35px'}
    ${({ withoutHorizontalPadding }) => (withoutHorizontalPadding ? 0 : '10px')};
`;

export const BreadcrumbsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 9px;
  background-color: #f8f8f8;
  padding: 6px;
`;

const BreadcrumbWrapper = styled.div`
  font-family: Poppins;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  transition: all 0.3s ease-in;
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  line-height: 1;
  gap: 4px;
  background-color: ${({ active }) => (active ? '#ffffff' : 'transparent')};
  cursor: pointer;
  color: ${({ validated }) => (validated ? '#004bc4' : 'inherit')};
  text-decoration: ${({ validated }) => (validated ? 'underline' : 'none')};
  text-decoration-style: ${({ validated }) => (validated ? 'solid' : 'none')};
  text-decoration-offset: ${({ validated }) => (validated ? '0%' : 'auto')};
  text-decoration-thickness: ${({ validated }) => (validated ? '0%' : 'auto')};

  &:hover {
    color: #004bc4;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-offset: 0%;
    text-decoration-thickness: 0%;
  }
`;

const StyledCheckIcon = styled(CheckIcon)`
  font-size: 20px !important;
  color: inherit;
`;

export const Breadcrumb = ({ children, ...props }) => (
  <BreadcrumbWrapper {...props}>
    <StyledCheckIcon />
    {children}
  </BreadcrumbWrapper>
);

export const SavingStatusText = styled.span`
  color: #6c7075;
  font-style: italic;
  font-weight: 300;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  line-height: 12px;
  width: 85px;
  text-align: right;
  height: 50px;
  display: flex;
  align-items: center;
`;

export const PausedBannerRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  margin-top: -15px;
`;

export const InfoContainerWrapper = styled.div`
  position: relative;
  width: fit-content;
  max-width: 100%;
  padding: 8px 36px 8px 20px;
  border-radius: 13px;
  background-color: #f8f8f8;
  font-family: Poppins;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  color: #004bc4;
`;

InfoContainerWrapper.CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e9ecef;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
